import type { IDisposable } from '@/engine/Utilities/IDisposable';
import ImageStack from '../../../../WasmModules/ImageStack';
import { WasmBuffer } from '@/engine/Utilities/WasmBuffer';
import type { IImageSliceRenderingContext } from './IImageSliceRenderingContext';

export class ImageSliceCanvasRenderingContext implements IDisposable, IImageSliceRenderingContext {
  private readonly _canvas: HTMLCanvasElement;

  private readonly _tempCanvas: HTMLCanvasElement;

  private readonly _drawingContext: CanvasRenderingContext2D;

  private readonly _tempDrawingContext: CanvasRenderingContext2D;

  private _tempImageData?: ImageData;

  private _tempBuffer?: WasmBuffer;

  public constructor() {
    this._canvas = document.createElement('canvas');
    this._tempCanvas = document.createElement('canvas');

    const drawingContext = this._canvas.getContext('2d');
    const tempDrawingContext = this._tempCanvas.getContext('2d');

    if (!drawingContext || !tempDrawingContext) {
      throw new Error('2D Drawing context is invalid');
    }

    this._drawingContext = drawingContext;
    this._tempDrawingContext = tempDrawingContext;
  }

  public dispose(): void {
    this._tempBuffer?.dispose();
    this._tempBuffer = undefined;
    this._tempImageData = undefined;
    this._resizeCanvases(0, 0);
  }

  public get canvas(): HTMLCanvasElement {
    return this._canvas;
  }

  private _resizeCanvases(width: number, height: number): void {
    this._canvas.width = width;
    this._canvas.height = height;
    this._tempCanvas.width = width;
    this._tempCanvas.height = height;
  }

  private _resizeImageData(width: number, height: number): void {
    this._tempImageData = this._tempDrawingContext.createImageData(width, height);
    this._tempBuffer?.dispose();
    this._tempBuffer = new WasmBuffer(ImageStack._module, width * height * 4);
  }

  public init(width: number, height: number): void {
    if (this.width == width && this.height == height) {
      return;
    }
    this._resizeCanvases(width, height);
    this._resizeImageData(width, height);
  }

  public get width(): number {
    return this._canvas.width;
  }

  public get height(): number {
    return this._canvas.height;
  }

  public render(renderer: (buffer: WasmBuffer) => void): void {
    if (!this._tempBuffer || !this._tempImageData) {
      throw new Error('Not initialized');
    }
    // [wasmArray -> tempCanvas -> canvas] rendering is required for desktop and mobile Safari
    renderer(this._tempBuffer);
    const array = new Uint8ClampedArray(ImageStack._module.HEAPU8.buffer, this._tempBuffer.ptr, this._tempBuffer.size);
    this._tempImageData.data.set(array);
    this._tempDrawingContext.putImageData(this._tempImageData, 0, 0);
    this._drawingContext.drawImage(this._tempCanvas, 0, 0, this._canvas.width, this._canvas.height);
  }
}
